import { TeamInfo } from '../TeamInfo';
import { RaceRules, StintAverageRow } from '../types';
import { parseLapTimeSeconds, stintMinutes } from './lapTime';

/**
 * Apex appends a running clock to the driver name (`"ANN SMITH [12:34]"`).
 * The backend strips it before building stint rows; the live `driver` field
 * still carries it, so anything comparing the two must strip it as well.
 */
export function normalizeDriverLabel(name: string): string {
  return (name ?? '').replace(/\s*\[\d+:\d+\]\s*$/, '').trim();
}

export interface DriverTime {
  driver: string;
  /** Minutes at the wheel so far. */
  minutes: number;
  lapCount: number;
  /** True for the driver currently in the kart. */
  isCurrent: boolean;
}

/**
 * Time per driver, derived from their stints.
 *
 * A stint's duration is its lap count times its average lap — the only per-driver
 * measure available, since Apex reports stint age for the *kart*, not the
 * driver. For the stint in progress the kart's own clock is used instead, which
 * is both more accurate and includes the out-lap.
 */
export function driverTimes(team: TeamInfo): DriverTime[] {
  const currentLabel = normalizeDriverLabel(team.driver);
  const byDriver = new Map<string, { minutes: number; lapCount: number }>();

  const add = (driver: string, minutes: number, lapCount: number): void => {
    const key = normalizeDriverLabel(driver);
    if (!key) return;
    const entry = byDriver.get(key) ?? { minutes: 0, lapCount: 0 };
    entry.minutes += minutes;
    entry.lapCount += lapCount;
    byDriver.set(key, entry);
  };

  const rows: StintAverageRow[] = team.stintAverages ?? [];
  for (const row of rows) {
    if (row.kind === 'current') {
      const kartStint = team.inBox ? null : stintMinutes(team.onTrackTime);
      const fromLaps = estimateStintMinutes(row);
      add(row.driver, kartStint ?? fromLaps, row.lapCount);
      continue;
    }
    add(row.driver, estimateStintMinutes(row), row.lapCount);
  }

  return Array.from(byDriver.entries())
    .map(([driver, v]) => ({
      driver,
      minutes: v.minutes,
      lapCount: v.lapCount,
      isCurrent: driver === currentLabel,
    }))
    .sort((a, b) => b.minutes - a.minutes);
}

function estimateStintMinutes(row: StintAverageRow): number {
  const average = parseLapTimeSeconds(row.averageLap);
  if (average === null || row.lapCount <= 0) return 0;
  return (average * row.lapCount) / 60;
}

export type ComplianceStatus =
  | 'ok'
  | 'short'
  | 'at-risk'
  | 'approaching-max'
  | 'over-max';

export interface DriverCompliance extends DriverTime {
  minMinutes: number | null;
  maxMinutes: number | null;
  /** Minutes still owed before reaching the minimum. */
  shortfallMinutes: number;
  /** Minutes left before hitting the maximum; null when unbounded. */
  headroomMinutes: number | null;
  status: ComplianceStatus;
}

export interface TeamCompliance {
  drivers: DriverCompliance[];
  /** Minutes the whole team still owes to satisfy every minimum. */
  totalShortfallMinutes: number;
  /**
   * False when the minimums can no longer all be met in the time left. Only one
   * driver can be in the kart at a time, so the shortfalls compete for the same
   * remaining minutes.
   */
  feasible: boolean;
}

/** Within this much of the cap, a driver is flagged before they breach it. */
const APPROACHING_MAX_MINUTES = 10;

export interface DriverBounds {
  minMinutes: number | null;
  maxMinutes: number | null;
}

/**
 * Compare time driven against the regulations.
 *
 * The interesting check is not "who is over" — that is visible — but whether the
 * minimums are still *reachable*. A driver who needs 40 more minutes with 30
 * left in the race has already failed, and nothing in the timing screen says so.
 */
export function checkCompliance(
  times: DriverTime[],
  rules: RaceRules,
  minutesRemaining: number | null,
  overrides: Map<string, DriverBounds> = new Map(),
): TeamCompliance {
  const drivers: DriverCompliance[] = times.map((t) => {
    const override = overrides.get(t.driver);
    const minMinutes = override?.minMinutes ?? rules.minDriverMinutes;
    const maxMinutes = override?.maxMinutes ?? rules.maxDriverMinutes;

    const shortfallMinutes =
      minMinutes === null ? 0 : Math.max(0, minMinutes - t.minutes);
    const headroomMinutes =
      maxMinutes === null ? null : maxMinutes - t.minutes;

    let status: ComplianceStatus = 'ok';
    if (headroomMinutes !== null && headroomMinutes < 0) {
      status = 'over-max';
    } else if (
      headroomMinutes !== null &&
      headroomMinutes <= APPROACHING_MAX_MINUTES
    ) {
      status = 'approaching-max';
    } else if (shortfallMinutes > 0) {
      status =
        minutesRemaining !== null && shortfallMinutes > minutesRemaining
          ? 'at-risk'
          : 'short';
    }

    return {
      ...t,
      minMinutes,
      maxMinutes,
      shortfallMinutes,
      headroomMinutes,
      status,
    };
  });

  const totalShortfallMinutes = drivers.reduce(
    (sum, d) => sum + d.shortfallMinutes,
    0,
  );

  return {
    drivers,
    totalShortfallMinutes,
    feasible:
      minutesRemaining === null || totalShortfallMinutes <= minutesRemaining,
  };
}

/**
 * Who is due in next, from the planned running order: the driver after whoever
 * is out now, wrapping round. Falls back to the first planned driver when the
 * current one is not in the plan.
 */
export function nextDriver(
  plannedOrder: string[],
  currentDriver: string,
): string | null {
  if (plannedOrder.length === 0) return null;

  const current = normalizeDriverLabel(currentDriver);
  const index = plannedOrder.findIndex(
    (d) => normalizeDriverLabel(d).toLowerCase() === current.toLowerCase(),
  );
  if (index === -1) return plannedOrder[0];

  return plannedOrder[(index + 1) % plannedOrder.length];
}
